import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardBody, Spinner } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { fetchWithFlaskAuth } from "../utils/api";
import { threatCard, threatCardHeader, threatCardBody, threatIconChip } from "./cardStyles";

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const STATUS_COLORS = {
  Online: { 
    color: '#198754', 
    bg: 'rgba(35, 145, 93, 0.1)', 
    border: 'rgba(25, 135, 84, 0.3)' 
  },
  Offline: { 
    color: '#dc3545', 
    bg: 'rgba(223, 64, 80, 0.1)', 
    border: 'rgba(220, 53, 69, 0.3)' 
  }
};

const AgentSecurityStatus = ({ loading }) => {
  const [agentStats, setAgentStats] = useState(null);
  const [agentLoading, setAgentLoading] = useState(true);

  useEffect(() => {
    fetchAgentStats();
  }, []);

  const fetchAgentStats = async () => {
    try {
      const data = await fetchWithFlaskAuth("/api/wazuh/agents");
      
      if (data && data.agents) {
        const agents = data.agents;
        
        const onlineAgents = agents.filter(a => {
          const status = (a.status || "").toLowerCase();
          return status.includes("active") || status.includes("online");
        }).length;

        const offlineAgents = agents.filter(a => {
          const status = (a.status || "").toLowerCase();
          return status.includes("disconnect") || status.includes("offline") || status.includes("never") || status.includes("pending");
        }).length;

        setAgentStats([
          { name: "Online", count: onlineAgents, ...STATUS_COLORS.Online },
          { name: "Offline", count: offlineAgents, ...STATUS_COLORS.Offline }
        ]);
      }
    } catch (err) {
      console.error("Error fetching agent stats:", err);
      setAgentStats([
        { name: "Online", count: 0, ...STATUS_COLORS.Online },
        { name: "Offline", count: 0, ...STATUS_COLORS.Offline }
      ]);
    } finally {
      setAgentLoading(false);
    }
  };

  if (loading || agentLoading) {
    return (
      <Card 
        className="h-100"
        style={{ 
          ...threatCard
        }}
      >
        <CardHeader style={threatCardHeader}>
          <div className="d-flex align-items-center gap-3">
            <div style={threatIconChip()}>
              <i className="ri-shield-check-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <h5 className="card-title mb-0">Agent Status</h5>
          </div>
        </CardHeader>
        <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px", ...threatCardBody }}>
          <Spinner style={{ color: ORANGE_COLORS.primary }} />
        </CardBody>
      </Card>
    );
  }

  const statuses = agentStats || [];
  const chartData = statuses.map(s => s.count);
  const chartLabels = statuses.map(s => s.name);
  const chartColors = statuses.map(s => s.color); // This now uses the correct colors: Green for Online, Red for Offline
  const totalAgents = chartData.reduce((sum, count) => sum + count, 0);

  const options = {
    chart: {
      type: "pie",
      toolbar: { show: false },
      fontFamily: "Poppins, sans-serif"
    },
    labels: chartLabels,
    colors: chartColors, // Green (#198754) for Online, Red (#dc3545) for Offline
    dataLabels: {
      enabled: true,
      formatter: (val, opts) => opts.w.config.series[opts.seriesIndex],
      style: {
        fontSize: '14px',
        fontWeight: '700',
        colors: ['#fff']
      },
      dropShadow: {
        enabled: true,
        top: 1,
        left: 1,
        blur: 1,
        opacity: 0.45
      }
    },
    legend: {
      show: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.1
        }
      }
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val) => `${val} agent${val !== 1 ? 's' : ''}`
      }
    }
  };

  return (
    <Card 
      className="h-100"
      style={{ 
        ...threatCard
      }}
    >
      <CardHeader style={threatCardHeader}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <div style={threatIconChip()}>
              <i className="ri-shield-check-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <h5 className="card-title mb-0">Agent Status</h5>
          </div>
          {totalAgents > 0 && (
            <span 
              className="badge" 
              style={{ 
                backgroundColor: 'rgba(255, 107, 53, 0.1)', 
                color: ORANGE_COLORS.primary,
                border: `1px solid ${ORANGE_COLORS.primary}`,
                fontSize: '11px'
              }}
            >
              {totalAgents} agents
            </span>
          )}
        </div>
      </CardHeader>
      <CardBody style={threatCardBody}>
        {totalAgents > 0 ? (
          <>
            {/* Pie Chart */}
            <div className="text-center mb-3">
              <ReactApexChart options={options} series={chartData} type="pie" height={180} />
            </div>
            
            {/* Status List */}
            <div className="mt-3 pt-3 border-top" style={{ borderColor: 'rgba(255, 107, 53, 0.2)' }}>
              {statuses.map((status, index) => (
                <div 
                  key={index} 
                  className="d-flex align-items-center justify-content-between mb-3 p-3 rounded"
                  style={{ backgroundColor: status.bg }}
                >
                  <div className="d-flex align-items-center">
                    <span 
                      className="rounded-circle me-3" 
                      style={{ 
                        width: "14px", 
                        height: "14px", 
                        backgroundColor: status.color,
                        display: "inline-block",
                        flexShrink: 0,
                        boxShadow: `0 0 0 3px ${status.bg}`
                      }}
                    ></span>
                    <span className="fw-medium" style={{ fontSize: "14px", color: '#495057' }}>
                      {status.name}
                    </span>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold" style={{ fontSize: "18px", color: status.color }}>
                      {status.count}
                    </div>
                    <div className="text-muted" style={{ fontSize: '10px' }}>
                      {totalAgents > 0 ? `${Math.round((status.count / totalAgents) * 100)}%` : '0%'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <div className="mx-auto mb-3" style={threatIconChip()}>
              <i className="ri-shield-check-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <p className="text-muted mb-0">No agent data available</p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default AgentSecurityStatus;
